import React from "react";
import {
  StyleSheet,
  Modal,
  ScrollView,
  TouchableOpacity,
  View,
} from "react-native";
import Text from "@components/CustomText";
import { colors } from "@constants/theme";
import BottomButton from "./BottomButton";

/**
 * "Polybags required" confirmation shown just before Mark OFD, when any of the
 * selected customers needs poly bags.
 *
 * The counts come from `polybag.count` on the OFD list response, which the
 * backend reads from the value computed at pick time — the CP is not asked to
 * enter anything. Display only: the dialog is not enforced server-side, so its
 * CTA is deliberately the sole caller of the submit.
 */
const PolybagModal = ({
  visible = false,
  totalBags = 0,
  rows = [],
  selectedCount = 0,
  loading = false,
  onClose = () => {},
  onConfirm = () => {},
}) => (
  <Modal
    animationType="fade"
    transparent={true}
    visible={visible}
    onRequestClose={onClose}
  >
    <View style={styles.overlay}>
      <View style={styles.card}>
        <View style={styles.header}>
          <Text variant="caption_1" style={styles.eyebrow}>
            POLYBAGS REQUIRED
          </Text>
          <Text variant="heading_24" style={styles.total}>
            {totalBags}
          </Text>
          <Text variant="body_text_3" style={styles.subtitle}>
            {`across ${rows.length} of ${selectedCount} selected customer${
              selectedCount === 1 ? "" : "s"
            }`}
          </Text>
        </View>

        {/* Count is in the header because the list scrolls: without it a CP can
            read the first few rows as the whole list and leave bags behind. */}
        <Text variant="caption_1" style={styles.listHeader}>
          {`CUSTOMER WISE (${rows.length})`}
        </Text>

        <ScrollView
          style={styles.list}
          nestedScrollEnabled={true}
          showsVerticalScrollIndicator={true}
        >
          {rows.map((row) => (
            <View key={row.key} style={styles.row}>
              <View style={styles.rowLeft}>
                <Text
                  variant="body_text_2"
                  numberOfLines={1}
                  style={styles.userName}
                >
                  {row.userName}
                </Text>
              </View>
              <Text variant="body_text_2" style={styles.bags}>
                {`${row.count} bag${row.count === 1 ? "" : "s"}`}
              </Text>
            </View>
          ))}
        </ScrollView>

        <View style={styles.footer}>
          <TouchableOpacity
            activeOpacity={0.8}
            disabled={loading}
            onPress={onClose}
            style={styles.backButton}
          >
            <Text variant="title_16" style={styles.backText}>
              Back
            </Text>
          </TouchableOpacity>
          <View style={styles.confirmButton}>
            <BottomButton
              onPress={onConfirm}
              title="Taken, mark OFD"
              loading={loading}
              showBottomContainer={false}
            />
          </View>
        </View>
      </View>
    </View>
  </Modal>
);

export default PolybagModal;

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: colors.modalBackground,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  card: {
    width: "100%",
    backgroundColor: colors.white,
    borderRadius: 8,
    overflow: "hidden",
  },
  header: {
    paddingTop: 24,
    paddingBottom: 20,
    paddingHorizontal: 20,
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: colors.lightGrey,
  },
  eyebrow: {
    color: colors.black06,
    letterSpacing: 1,
  },
  total: {
    color: colors.mediumBlack,
    fontSize: 48,
    lineHeight: 56,
    marginTop: 4,
  },
  subtitle: {
    color: colors.grey,
    marginTop: 4,
    textAlign: "center",
  },
  listHeader: {
    color: colors.black06,
    letterSpacing: 1,
    paddingHorizontal: 20,
    paddingTop: 14,
    paddingBottom: 4,
  },
  list: {
    maxHeight: 240,
    paddingHorizontal: 20,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 11,
    borderBottomWidth: 1,
    borderBottomColor: colors.lightGrey,
  },
  rowLeft: {
    flex: 1,
    paddingRight: 12,
  },
  userName: {
    color: colors.mediumBlack,
  },
  bags: {
    color: colors.mediumBlack,
  },
  footer: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: colors.lightGrey,
  },
  backButton: {
    width: "34%",
    paddingVertical: 10,
    marginRight: 10,
    alignItems: "center",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.black02,
  },
  backText: {
    color: colors.mediumBlack,
  },
  confirmButton: {
    flex: 1,
  },
});
